/**
 * Maps raw backend error strings to user-friendly messages.
 *
 * Every error the user sees should read as a clear English sentence
 * with an action suggestion. No Python tracebacks, no internal codes.
 */

const ERROR_MAP: [RegExp, string][] = [
  [
    /deal has no property data/i,
    "No property data available yet. Upload a rent roll to get started.",
  ],
  [
    /no property data/i,
    "Upload documents first to generate a property model.",
  ],
  [
    /tenant index out of range/i,
    "The requested tenant could not be found. Try refreshing the page.",
  ],
  [
    /auto[- ]?project failed/i,
    "The cash flow model could not be generated from the uploaded documents. Check the Documents tab for parse issues.",
  ],
  [
    /property model could not be generated/i,
    "The cash flow model could not be generated from the uploaded documents. Check the Documents tab for parse issues.",
  ],
  [
    /^Failed: /i,
    "Something went wrong applying those changes. Try making one change at a time.",
  ],
  [
    /network\s*error/i,
    "Unable to reach the server. Check your connection and try again.",
  ],
  [
    /timeout|timed?\s*out/i,
    "The request took too long. Try again or simplify the operation.",
  ],
];

/**
 * Convert a raw backend or network error string into a polished,
 * user-facing message. Returns the original string (stripped of any
 * traceback noise) if no pattern matches.
 */
export function friendlyError(raw: string): string {
  if (!raw) return "An unexpected error occurred. Please try again.";

  for (const [pattern, friendly] of ERROR_MAP) {
    if (pattern.test(raw)) return friendly;
  }

  // Strip Python traceback content
  const tbIdx = raw.indexOf("Traceback");
  if (tbIdx >= 0) {
    const before = raw.slice(0, tbIdx).trim();
    return before || "An unexpected error occurred. Please try again.";
  }
  if (/File "\//.test(raw)) {
    return "An unexpected error occurred. Please try again.";
  }

  return raw;
}
